import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import { promoCodeEventsService } from "../../services/promo-code-events.service";
import { PromoCodeEvent, PromoCode, CreatePromoCodeEventDto } from "../../interfaces/promo-code-event.interface";
import { useConfirm } from "../../hooks/useConfirm";

const EMPTY_FORM: CreatePromoCodeEventDto = { name: "", prefix: "", pointsPerCode: 100 };

function getErrMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as any).response?.data;
    if (r?.message) return Array.isArray(r.message) ? r.message.join(", ") : r.message;
  }
  return fallback;
}

const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

const PromoCodeEventsPage = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();

  const [events, setEvents] = useState<PromoCodeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PromoCodeEvent | null>(null);
  const [form, setForm] = useState<CreatePromoCodeEventDto>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const setF = (patch: Partial<CreatePromoCodeEventDto>) => setForm((f) => ({ ...f, ...patch }));

  const [viewEvent, setViewEvent] = useState<PromoCodeEvent | null>(null);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [codesPage, setCodesPage] = useState(1);
  const [codesTotal, setCodesTotal] = useState(0);
  const [codesLoading, setCodesLoading] = useState(false);

  const [generateCount, setGenerateCount] = useState(100);
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await promoCodeEventsService.list(1, 100);
      setEvents(res.data);
    } catch {
      setError(t("load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const loadCodes = useCallback(async (eventId: string, page: number) => {
    setCodesLoading(true);
    try {
      const res = await promoCodeEventsService.listCodes(eventId, page, 50);
      setCodes(res.data);
      setCodesTotal(res.meta.total);
    } finally {
      setCodesLoading(false);
    }
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(""); setShowForm(true); };

  const openEdit = (e: PromoCodeEvent) => {
    setEditing(e);
    setForm({ name: e.name, prefix: e.prefix ?? "", pointsPerCode: e.pointsPerCode });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setFormError("");
    try {
      const payload = { ...form, prefix: form.prefix?.trim() || undefined };
      if (editing) {
        await promoCodeEventsService.update(editing.id, payload);
      } else {
        await promoCodeEventsService.create(payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(getErrMsg(err, t("save_error")));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (e: PromoCodeEvent) => {
    const key = e.isActive ? "confirm_deactivate" : "confirm_activate";
    const ok = await confirm(t(key));
    if (!ok) return;
    try {
      await promoCodeEventsService.update(e.id, { isActive: !e.isActive });
      await load();
    } catch {
      setError(t("save_error"));
    }
  };

  const handleDelete = async (e: PromoCodeEvent) => {
    if (e.redeemedCount > 0) return;
    const ok = await confirm(t("confirmDelete"));
    if (!ok) return;
    try {
      await promoCodeEventsService.remove(e.id);
      await load();
    } catch {
      setError(t("delete_error"));
    }
  };

  const openViewCodes = async (e: PromoCodeEvent) => {
    setViewEvent(e);
    setCodesPage(1);
    setGenerateMsg("");
    await loadCodes(e.id, 1);
  };

  const handleGenerate = async () => {
    if (!viewEvent) return;
    setGenerating(true);
    setGenerateMsg("");
    try {
      const res = await promoCodeEventsService.generateCodes(viewEvent.id, generateCount);
      setGenerateMsg(t("generated_success", { count: res.generated }));
      await load();
      await loadCodes(viewEvent.id, 1);
    } catch (err) {
      setGenerateMsg(getErrMsg(err, t("save_error")));
    } finally {
      setGenerating(false);
    }
  };

  const codePageCount = Math.ceil(codesTotal / 50);

  return (
    <Layout>
      {ConfirmDialog}
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t("promo_code_events")}</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("promo_code_events_desc")}</p>
          </div>
          <Button onClick={openCreate}>{t("add_promo_code_event")}</Button>
        </div>

        {error && <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">{error}</div>}

        <Card>
          {loading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">{t("loading")}</div>
          ) : events.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">{t("no_records")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("name")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("prefix")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("points_per_code")}</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">{t("total_codes")} / {t("redeemed_count")} / {t("remaining_count")}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        <span>{e.name}</span>
                        {!e.isActive && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">{t("inactive")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono">{e.prefix ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{e.pointsPerCode.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{e.totalCodes} / {e.redeemedCount} / {e.remainingCount}</td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <button onClick={() => openViewCodes(e)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">{t("view_codes")}</button>
                        <button onClick={() => handleToggleActive(e)} className="text-yellow-600 dark:text-yellow-400 hover:underline text-xs">
                          {e.isActive ? t("deactivate") : t("activate")}
                        </button>
                        <button onClick={() => openEdit(e)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs">{t("edit")}</button>
                        {e.redeemedCount > 0 ? (
                          <span className="text-gray-300 dark:text-gray-600 text-xs cursor-not-allowed" title={t("delete_blocked_redeemed")}>{t("delete")}</span>
                        ) : (
                          <button onClick={() => handleDelete(e)} className="text-red-500 dark:text-red-400 hover:underline text-xs">{t("delete")}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={showForm} onClose={() => setShowForm(false)} title={editing ? t("edit_promo_code_event") : t("add_promo_code_event")}>
          <div className="space-y-4">
            {formError && <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">{formError}</div>}
            <div>
              <label className={labelClass}>{t("name")} *</label>
              <input className={inputClass} value={form.name} onChange={(e) => setF({ name: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>{t("prefix")}</label>
              <input className={inputClass} placeholder="MGO" value={form.prefix ?? ""} onChange={(e) => setF({ prefix: e.target.value.toUpperCase() })} />
              <p className="mt-1 text-xs text-gray-400">{t("prefix_hint")}</p>
            </div>
            <div>
              <label className={labelClass}>{t("points_per_code")} *</label>
              <input type="number" min={1} className={inputClass} value={form.pointsPerCode} onChange={(e) => setF({ pointsPerCode: +e.target.value })} />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>{t("cancel")}</Button>
              <Button onClick={handleSubmit} disabled={saving || !form.name || !form.pointsPerCode}>
                {saving ? t("saving") : t("save")}
              </Button>
            </div>
          </div>
        </Dialog>

        <Dialog open={!!viewEvent} onClose={() => setViewEvent(null)} title={viewEvent ? `${viewEvent.name} — ${t("view_codes")}` : ""}>
          <div className="space-y-4">
            <div className="flex items-end gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <label className={labelClass}>{t("generate_codes_count")}</label>
                <input type="number" min={1} max={5000} className={inputClass} value={generateCount} onChange={(e) => setGenerateCount(+e.target.value)} />
              </div>
              <Button onClick={handleGenerate} disabled={generating || generateCount < 1 || generateCount > 5000}>
                {generating ? t("generating") : t("generate_codes")}
              </Button>
            </div>
            {generateMsg && <div className="p-2 rounded text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20">{generateMsg}</div>}

            {codesLoading ? (
              <div className="py-4 text-center text-gray-500 dark:text-gray-400 text-sm">{t("loading")}</div>
            ) : codes.length === 0 ? (
              <div className="py-4 text-center text-gray-500 dark:text-gray-400 text-sm">{t("no_records")}</div>
            ) : (
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t("code")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t("status")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t("redeemed_by")}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t("redeemed_at")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => (
                      <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{c.code}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${c.isRedeemed ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400" : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"}`}>
                            {c.isRedeemed ? t("code_status_redeemed") : t("code_status_available")}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{c.redeemedByUserId ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{c.redeemedAt ? new Date(c.redeemedAt).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {codePageCount > 1 && (
              <div className="flex gap-2 justify-center pt-2">
                <button disabled={codesPage <= 1} onClick={() => { const p = codesPage - 1; setCodesPage(p); loadCodes(viewEvent!.id, p); }} className="px-3 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">←</button>
                <span className="px-3 py-1 text-xs text-gray-500">{codesPage} / {codePageCount}</span>
                <button disabled={codesPage >= codePageCount} onClick={() => { const p = codesPage + 1; setCodesPage(p); loadCodes(viewEvent!.id, p); }} className="px-3 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">→</button>
              </div>
            )}
          </div>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PromoCodeEventsPage;
